import React from 'react';
import c from 'classnames';
import config from 'react-global-configuration';
import { useParallax } from 'react-scroll-parallax';
import { prefersReducedMotion } from '@uncinc/uncinc-react-kitchen-sink';

import { DOMAIN_KVNL, IS_CLIENT } from '../../config/constants';
import Paragraph from './Paragraph';
import Container from './Container';
import Expert from './Expert';
import Hero from './Hero';

import './style.scss';

export interface SwirlIllustrationsProps {
  variant: 'home' | 'hero' | 'paragraph' | 'container' | 'fullpage' | 'expert' | 'quiz_end' | 'quiz_info' | number;
  colorVariant?: 'one' | 'two' | 'three' | 'four' | 'five';
  parallaxSpeed?: number;
}

const SwirlIllustrationsInner: React.FC<Omit<SwirlIllustrationsProps, 'parallaxSpeed'>> = (props) => {
  const {
    variant,
    colorVariant,
  } = props;

  switch (variant) {
    case 'paragraph': {
      return (
        <Paragraph colorVariant={colorVariant} />
      );
    }

    case 'container': {
      return (
        <Container />
      );
    }

    case 'expert': {
      return (
        <Expert />
      );
    }

    case 'hero': {
      return (
        <Hero />
      );
    }

    default: {
      return (
        <>
          <div className="SwirlIllustrations__item SwirlIllustrations__left" />
          <div className="SwirlIllustrations__item SwirlIllustrations__right" />
        </>
      );
    }
  }
};

const SwirlIllustrationsSimpleLazy: React.FC<Omit<SwirlIllustrationsProps, 'parallaxSpeed'>> = ({
  variant,
  colorVariant,
}) => {
  const currentDomain = React.useMemo<string>(() => config.get('CURRENT_DOMAIN') as string, []);
  const classNames = c('SwirlIllustrations', {
    [`SwirlIllustrations--variant--${variant}`]: variant,
  });

  if (currentDomain !== DOMAIN_KVNL) {
    return false;
  }

  return (
    <div className={classNames}>
      <div className="SwirlIllustrations__inner">
        <SwirlIllustrationsInner variant={variant} colorVariant={colorVariant} />
      </div>
    </div>
  );
};

const SwirlIllustrationsParallaxLazy: React.FC<SwirlIllustrationsProps> = ({
  variant,
  colorVariant,
  parallaxSpeed,
}) => {
  const { ref } = useParallax({
    speed: parallaxSpeed || 10,
    disabled: prefersReducedMotion(),
  });
  const currentDomain = React.useMemo<string>(() => config.get('CURRENT_DOMAIN') as string, []);
  const classNames = c('SwirlIllustrations', {
    [`SwirlIllustrations--variant--${variant}`]: variant,
  });

  if (currentDomain !== DOMAIN_KVNL) {
    // We have to assign the ref to something,
    // even if we don't want to render anything.
    // That's why we add the className "hidden".
    return <div ref={ref as React.RefObject<HTMLDivElement>} className="hidden" />;
  }

  return (
    <div className={classNames}>
      <div ref={ref as React.RefObject<HTMLDivElement>} className="SwirlIllustrations__inner">
        <SwirlIllustrationsInner variant={variant} colorVariant={colorVariant} />
      </div>
    </div>
  );
};

// Wrapper to only render client-side.
const SwirlIllustrations: React.FC<SwirlIllustrationsProps> = (props) => {
  const { parallaxSpeed = 0 } = props;

  return (
    <div>
      {IS_CLIENT && parallaxSpeed !== 0 && <SwirlIllustrationsParallaxLazy {...props} />}
      {IS_CLIENT && parallaxSpeed === 0 && <SwirlIllustrationsSimpleLazy {...props} />}
    </div>
  );
};

export default SwirlIllustrations;
